import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { initFirebaseApp, isFirebaseConfigured } from '../firebaseApp';

const AuthContext = createContext(null);

function getAuthInstance() {
  const app = initFirebaseApp();
  if (!app) {
    const error = new Error('auth-not-configured');
    error.code = 'auth-not-configured';
    throw error;
  }
  return getAuth(app);
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(isFirebaseConfigured());
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (!firebaseReady) {
      setLoading(false);
      return undefined;
    }

    let unsubscribe = null;
    try {
      const auth = getAuthInstance();
      unsubscribe = onAuthStateChanged(auth, (user) => {
        setFirebaseUser(user || null);
        setLoading(false);
      });
    } catch (err) {
      console.warn('Failed to init Firebase auth', err);
      setAuthError('Authentication is not configured.');
      setLoading(false);
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [firebaseReady]);

  const loginWithGoogle = useCallback(async () => {
    setAuthError('');
    try {
      const auth = getAuthInstance();
      await signInWithPopup(auth, createGoogleProvider());
    } catch (err) {
      console.error('Google sign-in failed', err);
      setAuthError(err?.message || 'Sign in failed.');
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setAuthError('');
    try {
      const auth = getAuthInstance();
      await signOut(auth);
    } catch (err) {
      console.error('Sign out failed', err);
      setAuthError(err?.message || 'Sign out failed.');
      throw err;
    }
  }, []);

  const getIdToken = useCallback(async () => {
    if (!firebaseUser) {
      return null;
    }
    return firebaseUser.getIdToken();
  }, [firebaseUser]);

  const value = useMemo(() => ({
    firebaseUser,
    firebaseReady,
    loading,
    authError,
    loginWithGoogle,
    logout,
    getIdToken
  }), [firebaseUser, firebaseReady, loading, authError, loginWithGoogle, logout, getIdToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
